import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip, Polyline, useMap, useMapEvents } from "react-leaflet";
import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Raggruppa i marker vicini/sovrapposti mostrando un conteggio (si separano con lo zoom).
function ClusterLayer({ points, onPointClick, onClusterClick }) {
  const map = useMap();
  const cbRef = useRef(onPointClick);
  cbRef.current = onPointClick;
  const ccRef = useRef(onClusterClick);
  ccRef.current = onClusterClick;
  useEffect(() => {
    const group = L.markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 45, spiderfyOnMaxZoom: false, zoomToBoundsOnClick: false, showCoverageOnHover: false });
    points.forEach((pt) => {
      const m = L.circleMarker([pt.lat, pt.lng], {
        radius: pt.radius || 6, color: pt.borderColor || "#fff",
        weight: pt.borderWeight || 1, fillColor: pt.color || "#2563eb", fillOpacity: 0.9,
      });
      m.pid = pt.id;
      m.pmLabel = pt.label || pt.tooltip || "";
      if (pt.tooltip) m.bindTooltip(pt.tooltip, { direction: "top" });
      if (cbRef.current) m.on("click", () => cbRef.current(pt.id));
      group.addLayer(m);
    });
    group.on("clusterclick", (e) => {
      const clu = e.layer;
      const markers = clu.getAllChildMarkers();
      const ids = markers.map((cm) => cm.pid).filter(Boolean);
      if (ccRef.current) ccRef.current(ids);
      const b = clu.getBounds();
      const diag = b.getNorthEast().distanceTo(b.getSouthWest());
      // Se i punti sono distinti e c'è margine di zoom, avvicina per separarli
      if (diag > 60 && map.getZoom() < map.getMaxZoom()) {
        map.fitBounds(b.pad(0.25));
        return;
      }
      // Punti sovrapposti (o zoom al massimo): mostra l'elenco dei nomi cliccabili
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      const shown = markers.slice(0, 80);
      const rows = shown.map((cm) => `<button type="button" class="pm-item" data-id="${cm.pid}">${esc(cm.pmLabel)}</button>`).join("");
      const more = markers.length > 80 ? `<div class="pm-more">+${markers.length - 80} altri — usa i filtri per restringere</div>` : "";
      const html = `<div class="pm-list"><div class="pm-title">${markers.length} prospect in questo punto</div>${rows}${more}</div>`;
      const popup = L.popup({ maxHeight: 260, minWidth: 220, className: "pm-popup", autoPan: true }).setLatLng(clu.getLatLng()).setContent(html).openOn(map);
      setTimeout(() => {
        const el = popup.getElement();
        if (!el) return;
        el.querySelectorAll(".pm-item").forEach((btn) => {
          btn.addEventListener("click", () => { if (cbRef.current) cbRef.current(btn.getAttribute("data-id")); map.closePopup(popup); });
        });
      }, 30);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [points, map]);
  return null;
}

// Marker non-cluster: a zoom sufficiente mostra il logo del partner (se disponibile), altrimenti un pallino.
function AdaptiveMarkers({ points, onPointClick, logoZoom = 11 }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  return points.map((pt) => {
    if (pt.logoUrl && zoom >= logoZoom) {
      const icon = L.divIcon({
        className: "pm-logo-icon",
        html: `<img src="${pt.logoUrl}" style="width:38px;height:38px;object-fit:contain;background:#fff;border:2px solid ${pt.color || "#16a34a"};border-radius:8px;padding:2px;box-shadow:0 1px 4px rgba(0,0,0,.4)" onerror="this.style.display='none'"/>`,
        iconSize: [42, 42], iconAnchor: [21, 21],
      });
      return (
        <Marker key={pt.id} position={[pt.lat, pt.lng]} icon={icon}
          eventHandlers={onPointClick ? { click: () => onPointClick(pt.id) } : undefined}>
          {pt.tooltip && <Tooltip direction="top" offset={[0, -20]}><div className="text-xs">{pt.tooltip}</div></Tooltip>}
        </Marker>
      );
    }
    return (
      <CircleMarker key={pt.id} center={[pt.lat, pt.lng]} radius={pt.radius || 6}
        pathOptions={{ color: pt.borderColor || "#fff", weight: pt.borderWeight || 1, fillColor: pt.color || "#2563eb", fillOpacity: 0.9 }}
        eventHandlers={onPointClick ? { click: () => onPointClick(pt.id) } : undefined}>
        {pt.tooltip && <Tooltip direction="top" offset={[0, -2]}><div className="text-xs">{pt.tooltip}</div></Tooltip>}
      </CircleMarker>
    );
  });
}

// Generic geolocation mini-map. points: [{id,lat,lng,color,radius,tooltip}]
export default function PointsMap({ points = [], paths = [], height = 340, center = [42.2, 12.5], zoom = 5, legend = null, testid = "points-map", onPointClick = null, cluster = false, onClusterClick = null }) {
  return (
    <div className="relative z-0 overflow-hidden rounded-md border border-border" style={{ height }} data-testid={testid}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {paths.map((p, i) => (
          <Polyline key={`path-${i}`} positions={p.points} pathOptions={{ color: p.color || "#a855f7", weight: 3, opacity: 0.85, dashArray: p.dashArray }} />
        ))}
        {cluster ? (
          <ClusterLayer points={points} onPointClick={onPointClick} onClusterClick={onClusterClick} />
        ) : (
          <AdaptiveMarkers points={points} onPointClick={onPointClick} />
        )}
      </MapContainer>
      {legend && <div className="absolute bottom-2 left-2 z-[1000] flex flex-wrap items-center gap-3 rounded-md border border-border bg-background/90 px-2 py-1 text-[11px] shadow">{legend}</div>}
    </div>
  );
}
